import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  Injector,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { filter, map, switchMap, takeUntil, timeout } from 'rxjs/operators';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import * as PaymentValidators from '../../../../misc/payment-validators';
import { AbstractMethodComponent } from '../abstract-method.component';
import { Subject } from 'rxjs';
import { getInvalidFieldListFromFormGroup } from '../../../../misc/get-invalid-field-list-from-form-group';
import { MonitoringService, OrderFormValidationResult } from '@orderform/widgets';

export const getCreditCardTypeFromNumber = function (creditCardNumber: string): string | null {
  const no4 = parseInt(creditCardNumber.slice(0, 4), 10);
  const no6 = parseInt(creditCardNumber.slice(0, 6), 10);
  if (creditCardNumber.length > 0 && creditCardNumber.charAt(0) === '4') {
    return 'visa';
  } else if (
    (creditCardNumber.length > 1 && creditCardNumber.match(/^5[1-5]/)) ||
    (creditCardNumber.length > 3 && no4 >= 2221 && no4 <= 2720)
  ) {
    return 'mastercard';
  } else if (
    (creditCardNumber.length > 1 && creditCardNumber.match(/^(6011|64|65)/)) ||
    (no6 >= 622126 && no6 <= 622925)
  ) {
    return 'discover';
  } else if (creditCardNumber.length > 1 && creditCardNumber.match(/^(34|37)/)) {
    return 'americanexpress';
  }
  return null;
};

@Component({
  selector: 'orderform-payment-method-umsbanking',
  templateUrl: './umsbanking.component.html',
  styleUrls: ['./umsbanking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmsbankingComponent),
      multi: true,
    },
  ],
})
export class UmsbankingComponent extends AbstractMethodComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() config: { debug: string };
  form = new FormGroup({
    number: new FormControl('', [Validators.required, PaymentValidators.creditCardNumber]),
    expires: new FormControl('', [Validators.required, PaymentValidators.creditCardExpire]),
    cvv: new FormControl('', [Validators.required, PaymentValidators.creditCardCvc]),
  });
  creditCardType$ = this.form.get('number').valueChanges.pipe(map(getCreditCardTypeFromNumber));

  private _subscriptionFormValidation = this._orderformFascade.formValidation.subscribe(
    () => this._onFormValidation(),
    this._element
  );
  private _subscriptionAfterSubmit = this._orderformFascade.afterSubmit.subscribe(() => this._onAfterSubmit());
  private _destroy = new Subject<void>();
  public expiresPlaceholder = '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2);

  constructor(
    private _orderformFascade: OrderformFascade,
    private _cdr: ChangeDetectorRef,
    private monitoringService: MonitoringService,
    @Inject(LOCALE_ID) private _locale: string,
    injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.creditCardType$.pipe(takeUntil(this._destroy)).subscribe((cardType) => {
      this._paymentDataChanged.next({
        valid: true,
        creditcard_creditcard_type: cardType,
      });
    });
  }

  public onInputFormatCreditCard(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\s/g, ''); // Remove existing spaces

    // Only allow digits
    value = value.replace(/\D/g, '');

    const cardType = getCreditCardTypeFromNumber(value);

    const maxLengths = {
      visa: 16,
      mastercard: 16,
      americanexpress: 15,
      discover: 16,
    };

    const maxLength = cardType ? maxLengths[cardType] : 19;

    if (value.length > maxLength) {
      value = value.slice(0, maxLength);
    }

    let formattedValue = value.replace(/(\d{4})(?=\d)/g, '$1 ');

    // Override for American Express: 4-6-5 format
    if (cardType === 'americanexpress') {
      if (value.length > 10) {
        formattedValue = value.replace(/(\d{4})(\d{6})(\d{1,5})/, '$1 $2 $3');
      } else if (value.length > 4) {
        formattedValue = value.replace(/(\d{4})(\d{1,6})/, '$1 $2');
      } else {
        formattedValue = value;
      }
    }

    this.form.get('number').setValue(formattedValue, { emitEvent: false });
  }

  public onInputFormatCvv(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cardType = getCreditCardTypeFromNumber(this.form.get('number').value || '');

    // Only allow digits
    let value = input.value.replace(/\D/g, '');

    // Amex uses 4-digit CVV, others use 3
    const maxLength = cardType === 'americanexpress' ? 4 : 3;

    if (value.length > maxLength) {
      value = value.slice(0, maxLength);
    }

    input.value = value;

    this.form.get('cvv').setValue(value, { emitEvent: false });
  }

  private _onAfterSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }

    return new Promise((resolve) => {
      this._orderformFascade.pending$
        .pipe(
          takeUntil(this._destroy),
          filter((pending) => !pending),
          switchMap(() => this._orderformFascade.paymentUrl$),
          timeout(7000)
        )
        .subscribe({
          next: (url) => {
            if (url && url.length > 0) {
              const expireFormatted = this.form.get('expires').value;
              const [month, yearSuffix] = expireFormatted.split('/', 2);
              const year = parseInt(yearSuffix, 10) + 2000;
              const htmlForm = document.createElement('form');
              htmlForm.setAttribute('action', url as string);
              htmlForm.setAttribute('method', 'POST');
              [
                { name: 'billing-cc-number', value: this.form.get('number').value },
                { name: 'billing-cc-exp', value: '' + month + '' + year },
                { name: 'cvv', value: this.form.get('cvv').value },
              ].forEach((field) => {
                const input = document.createElement('input');
                input.setAttribute('type', 'hidden');
                input.setAttribute('name', field.name);
                input.setAttribute('value', field.value);
                htmlForm.appendChild(input);
              });

              document.body.appendChild(htmlForm);
              htmlForm.submit();
              resolve({ valid: true });
            }
          },
          error: () => {
            if (this.config.debug) {
              console.log(this.config.debug);
            }
            const message = $localize`:@@payment_method.error.creditcard_data_missing:Bitte geben Sie Ihre Kreditkarten-Daten ein`;
            this._toaster.error(message);
            this.monitoringService.trackValidationError(message, 'frontend', 'umsbanking');
            this._paymentDataChanged.next({ valid: false });
            resolve({ valid: false });
          },
        });
    });
  }

  public onInputFormatExpires(event: Event) {
    const input = event.target as HTMLInputElement;
    const inputEvent = event as InputEvent;
    // Remove non-digits, but keep the slash
    input.value = input.value.replace(/[^\d\/]/g, '');

    if (inputEvent.inputType === 'deleteContentBackward' && input.value.endsWith('/')) {
      // Remove both the slash and the last digit
      input.value = input.value.slice(0, -2);
      this.form.get('expires').setValue(input.value, { emitEvent: false });
      return;
    }
    if (input.value.length > 1 && input.value.indexOf('/') === -1) {
      input.value = input.value + '/';
    }
    if (input.value.indexOf(' ') > 0) {
      input.value = input.value.replace(/\s/g, '');
    }
    if (input.value.length > 5) {
      input.value = input.value.slice(0, 5);
    }
    this.form.get('expires').setValue(input.value, { emitEvent: false });
  }

  private _onFormValidation(): Promise<OrderFormValidationResult> {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (!this.isActive() || this.form.valid) {
      return Promise.resolve({
        valid: true,
        component: 'ds-orderform-payment-umsbanking',
        invalidFieldList: [],
      });
    }

    Object.keys(this.form.controls).forEach((name) => {
      this.form.controls[name].markAsDirty();
    });
    this._cdr.detectChanges();
    this._paymentDataChanged.next({
      valid: this.form.valid,
    });

    return Promise.resolve({
      valid: false,
      component: 'ds-orderform-payment-umsbanking',
      invalidFieldList: getInvalidFieldListFromFormGroup(this.form),
    });
  }

  ngOnDestroy() {
    this._subscriptionFormValidation.unsubscribe();
    this._subscriptionAfterSubmit.unsubscribe();
    this._destroy.next();
  }
}
